import { useQueryClient } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import {
  Handshake,
  Search,
  X,
  Share2,
  ShoppingCart,
  Home,
  Calendar,
  Users,
  Database,
  IndianRupee,
  Eye,
  Lightbulb,
  LayoutDashboard,
  UsersRound,
  FolderKanban,
  Blocks,
  Apple,
  Gift,
  BookOpen,
  Video,
  Bell,
  Watch,
  Receipt,
  Award,
  ClipboardCheck,
  MessageCircle,
  Bot,
  Globe,
  Mic,
  Star,
  Stethoscope,
} from 'lucide-react';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUserProgramEnrollments } from '@/apis/users';
import Logo from '@/assets/login_logo.svg';
import Avtar from '@/assets/member-assets/homepage/Avatar.svg';
import HealthIcon from '@/assets/member-assets/homepage/health_icon.svg';
import HealthSnapshotIcon from '@/assets/member-assets/homepage/healthsnapshot.svg';
import HomeIcon from '@/assets/member-assets/homepage/home_black.svg';
import Logout from '@/assets/member-assets/homepage/Logout_Home.svg';
import ProductIcon from '@/assets/member-assets/homepage/product_icon.svg';
import TodoIcon from '@/assets/member-assets/homepage/todo_icon.svg';
import { prefetchConditionBuilderData } from '@/components/builder/hooks/prefetchBuilderData';
import MemberDefaultModel from '@/components/member/member-modals/MemberDefaultModel';
import { buildQuickLinksSection } from '@/components/sidebar/buildQuickLinksSection';
import { QuickLinksDrawer } from '@/components/sidebar/QuickLinksDrawer';
import { SidebarLogo } from '@/components/sidebar/SidebarLogo';
import { SidebarNavList } from '@/components/sidebar/SidebarNavList';
import { SidebarProfile } from '@/components/sidebar/SidebarProfile';
import { SidebarNavItem, ProgramEnrollment, CompactGroup } from '@/components/sidebar/types';
import { useSidebarPins } from '@/components/sidebar/useSidebarPins';
import {
  toTitleCase,
  formatRole,
  calculateProgramProgress,
  SENIOR_ADMIN_ROLES,
} from '@/components/sidebar/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { canAccessBackofficeSection } from '@/config/backoffice-permissions';
import { FEATURE_FLAGS, isFeatureEnabled } from '@/config/feature-flags';
import {
  canAccessEarlyDashboard,
  getNavigationItemPermission,
} from '@/config/navigation-permission-registry';
import {
  SidebarMode,
  SIDEBAR_WIDTHS,
  getSidebarBgColor,
  getSidebarStyle,
} from '@/config/sidebar-config';
import { isThriveProgram } from '@/constants';
import { useViewSettings } from '@/contexts/ViewSettingsContext';
import { listConsultationVideos } from '@/features/videos/api';
import { normalizePaginated } from '@/features/videos/utils';
import { useActingOnBehalf } from '@/hooks/useActingOnBehalf';
import { useMemberClient } from '@/hooks/useMemberClient';
import { usePermissions } from '@/hooks/usePermissions';
import { useUiPreferences } from '@/hooks/useUiPreferences';
import { filterNavigationByPermission } from '@/lib/filterNavigationByPermission';
import { logger } from '@/lib/logger';
import { signOutSession } from '@/lib/sign-out';
import { cn } from '@/lib/utils';
import { LEADS_ALLOWED_ROLES } from '@/pages/leads/leadConstants';
import { canAccessEarlyHub } from '@/pages/pmx-early-ops/earlyHubConstants';
import { useFeatureFlagsContext } from '@/providers/FeatureFlagsProvider';
import { RootState } from '@/redux/store';
import { PLATFORM_ASSISTANT_NAME } from '@/utils/platformChat';
import { matchesRoleAllowList } from '@/utils/roles';

interface InternalSidebarProps {
  sidebarMode: SidebarMode;
  onToggle: () => void;
  isMobileOpen: boolean;
  onMobileToggle: () => void;
}

interface InternalNavConfig {
  userRole: string;
  clientId: string;
  badgeText: string;
  hasPermission: (
    feature: import('@/utils/permissions').PermissionFeature,
    action: import('@/utils/permissions').PermissionAction
  ) => boolean;
  viewSettings: {
    showAnalytics?: boolean;
    showBuilder?: boolean;
  };
  featureFlags: {
    showHealthSnapshot: boolean;
    showVideos: boolean;
    showWearables: boolean;
    showDailyLog: boolean;
    showFamilyManagement: boolean;
    showGiftMembership: boolean;
    showMemberMarketplace: boolean;
    showMemberCarts: boolean;
    showNotifications: boolean;
    showClientTickets: boolean;
    showInAppChat: boolean;
  };
}

// Visible divider between the user's saved Quick Links and the rest of the
// nav, so a promoted entry reads as a shortcut rather than as part of the
// section below it.
const QUICK_LINKS_SEPARATOR: SidebarNavItem = {
  name: '--- QUICK LINKS DIVIDER ---',
  icon: Star,
  href: '#',
  isSeparator: true,
};

const buildWorkspaceSection = (config: InternalNavConfig): SidebarNavItem[] => {
  const { featureFlags, hasPermission, userRole } = config;
  const items: SidebarNavItem[] = [];

  items.push({
    name: '--- WORKSPACE HEADER ---',
    icon: Home,
    href: '#',
    isSectionHeader: true,
    sectionTitle: 'Workspace',
  });

  items.push({
    id: 'hub',
    name: 'Hub',
    icon: Share2,
    href: '/hub',
    shortcut: '⌘H',
    compactGroup: 'primary',
    requiredPermission: getNavigationItemPermission('hub'),
    subItems: [
      { name: 'Hub', href: '/hub' },
      ...(canAccessEarlyHub(userRole) ? [{ name: 'Early Hub', href: '/hub/early' }] : []),
      ...(canAccessEarlyDashboard(hasPermission)
        ? [{ name: 'Early Dashboard', href: '/app-dashboard' }]
        : []),
      {
        name: 'Bookings Dashboard',
        href: '/bookings-dashboard',
        requiredPermission: getNavigationItemPermission('consultation-dashboard'),
      },
    ],
  });
  items.push({
    id: 'business',
    name: 'Business Dashboard',
    compactName: 'Business',
    icon: IndianRupee,
    href: '/business-dashboard',
    compactGroup: 'primary',
    requiredPermission: getNavigationItemPermission('business'),
    subItems: [
      { name: 'Enrollments', href: '/business-dashboard' },
      ...(hasPermission('checkout', 'list')
        ? [
            {
              name: 'Registrations',
              href: '/business-dashboard?tab=registrations',
              requiredPermission: { feature: 'checkout' as const, action: 'list' as const },
            },
          ]
        : []),
      ...(hasPermission('checkout_support', 'list')
        ? [
            {
              name: 'Support',
              href: '/business-dashboard?tab=support',
              requiredPermission: { feature: 'checkout_support' as const, action: 'list' as const },
            },
          ]
        : []),
      ...(hasPermission('thyrocare_integration', 'create')
        ? [
            {
              name: 'Thyrocare',
              href: '/business-dashboard?tab=thyrocare',
              requiredPermission: {
                feature: 'thyrocare_integration' as const,
                action: 'create' as const,
              },
            },
          ]
        : []),
    ],
  });
  items.push({
    id: 'consultation',
    name: 'Bookings Dashboard',
    compactName: 'Bookings',
    icon: Stethoscope,
    href: '/bookings-dashboard',
    compactGroup: 'more',
    requiredPermission: getNavigationItemPermission('consultation'),
    subItems: [
      { name: 'Doctor consultations', href: '/bookings-dashboard' },
      { name: 'In-centre visits', href: '/bookings-dashboard?tab=in-centre' },
      { name: 'Therapy bookings', href: '/bookings-dashboard?tab=therapy' },
    ],
  });
  items.push({
    id: 'appointments',
    name: 'Appointments',
    compactName: 'Appts',
    icon: Calendar,
    href: '/appointments',
    shortcut: '⌘A',
    compactGroup: 'primary',
    requiredPermission: getNavigationItemPermission('appointments'),
    subItems: [
      { name: 'Appointments', href: '/appointments?tab=appointments' },
      { name: 'My Calendar', href: '/appointments?tab=my-calendar' },
      { name: 'Team Calendars', href: '/appointments?tab=team-calendars' },
    ],
  });
  if (matchesRoleAllowList(userRole, LEADS_ALLOWED_ROLES)) {
    items.push({
      id: 'leads',
      name: 'Discovery',
      compactName: 'Discovery',
      icon: Handshake,
      href: '/leads',
      compactGroup: 'primary',
    });
  }
  items.push({
    id: 'page-recordings',
    name: 'Voice recordings',
    compactName: 'Recordings',
    icon: Mic,
    href: '/page-recordings',
    compactGroup: 'more',
    requiredPermission: getNavigationItemPermission('page-recordings'),
  });
  if (featureFlags.showClientTickets) {
    items.push({
      id: 'tickets',
      name: 'Client Tickets',
      compactName: 'Tickets',
      icon: FolderKanban,
      href: '/client-tickets',
      compactGroup: 'primary',
      requiredPermission: getNavigationItemPermission('tickets'),
    });
  }
  if (featureFlags.showNotifications) {
    items.push({
      id: 'notifications',
      name: 'Notifications',
      compactName: 'Notifs',
      icon: Bell,
      href: '/notifications',
      compactGroup: 'primary',
      requiredPermission: getNavigationItemPermission('notifications'),
    });
  }

  return items;
};

const buildMemberPreviewSection = (config: InternalNavConfig): SidebarNavItem[] => {
  const { clientId, badgeText, featureFlags } = config;
  if (!clientId) return [];

  const items: SidebarNavItem[] = [];

  items.push({
    name: '--- MEMBER PREVIEW HEADER ---',
    icon: Home,
    href: '#',
    isSectionHeader: true,
    sectionTitle: 'Member Preview',
  });
  items.push({
    id: 'm-home',
    name: 'Member Home',
    compactName: 'Home',
    icon: HomeIcon,
    href: `/member/home/${clientId}`,
    compactGroup: 'primary',
    requiredPermission: getNavigationItemPermission('m-home'),
  });
  items.push({
    id: 'm-membership',
    name: 'Membership',
    icon: TodoIcon,
    href: `/member/todo/${clientId}`,
    badge: badgeText || '0/7',
    compactGroup: 'primary',
    requiredPermission: getNavigationItemPermission('m-membership'),
  });
  if (featureFlags.showHealthSnapshot) {
    items.push({
      id: 'm-health',
      name: 'Health Snapshot',
      compactName: 'Health',
      icon: HealthSnapshotIcon,
      href: `/member/health-snapshot/${clientId}`,
      compactGroup: 'primary',
      requiredPermission: getNavigationItemPermission('m-health'),
    });
  }
  items.push({
    id: 'm-reports',
    name: 'Reports',
    icon: HealthIcon,
    href: `/member/reports/${clientId}`,
    compactGroup: 'primary',
    requiredPermission: getNavigationItemPermission('m-reports'),
  });
  if (featureFlags.showWearables) {
    items.push({
      id: 'm-wearables',
      name: 'Wearables',
      icon: Watch,
      href: `/member/wearables/${clientId}`,
      compactGroup: 'more',
      requiredPermission: getNavigationItemPermission('m-wearables'),
    });
  }
  if (featureFlags.showDailyLog) {
    items.push({
      id: 'm-daily-log',
      name: 'My Daily Log',
      compactName: 'Daily Log',
      icon: ClipboardCheck,
      href: `/member/daily-checkin/${clientId}`,
      compactGroup: 'more',
      requiredPermission: getNavigationItemPermission('m-daily-log'),
    });
  }
  if (featureFlags.showNotifications) {
    items.push({
      id: 'm-notifs',
      name: 'Notifications',
      compactName: 'Notifs',
      icon: Bell,
      href: `/member/notifications/${clientId}`,
      compactGroup: 'more',
      requiredPermission: getNavigationItemPermission('m-notifs'),
    });
  }
  if (featureFlags.showVideos) {
    items.push({
      id: 'm-videos',
      name: 'Videos',
      icon: Video,
      href: `/member/videos/${clientId}`,
      compactGroup: 'more',
      requiredPermission: getNavigationItemPermission('m-videos'),
    });
  }
  if (featureFlags.showFamilyManagement) {
    items.push({
      id: 'm-family',
      name: 'My Family',
      compactName: 'Family',
      icon: UsersRound,
      href: `/member/family/${clientId}`,
      compactGroup: 'more',
      requiredPermission: getNavigationItemPermission('m-family'),
    });
  }
  if (featureFlags.showGiftMembership) {
    items.push({
      id: 'm-gift',
      name: 'Gift a Membership',
      compactName: 'Gift',
      icon: Gift,
      href: `/member/gift-thrive-early/${clientId}`,
      compactGroup: 'more',
      requiredPermission: getNavigationItemPermission('m-gift'),
    });
  }
  if (featureFlags.showMemberMarketplace) {
    items.push({
      id: 'm-marketplace',
      name: 'Marketplace',
      compactName: 'Market',
      icon: ProductIcon,
      href: `/member/marketplace/${clientId}`,
      compactGroup: 'more',
      requiredPermission: getNavigationItemPermission('m-marketplace'),
    });
    items.push({
      id: 'm-purchases',
      name: 'My Purchases',
      compactName: 'Purchases',
      icon: Receipt,
      href: `/member/my-purchases/${clientId}`,
      compactGroup: 'more',
      requiredPermission: getNavigationItemPermission('m-purchases'),
    });
    items.push({
      id: 'm-benefits',
      name: 'My Benefits',
      compactName: 'Benefits',
      icon: Award,
      href: `/member/benefits/${clientId}`,
      compactGroup: 'more',
      requiredPermission: getNavigationItemPermission('m-benefits'),
    });
  }
  if (featureFlags.showMemberCarts) {
    items.push({
      id: 'm-carts',
      name: 'My Carts',
      compactName: 'Carts',
      icon: ShoppingCart,
      href: `/member/my-carts/${clientId}`,
      compactGroup: 'more',
      requiredPermission: getNavigationItemPermission('m-carts'),
    });
  }
  if (featureFlags.showInAppChat) {
    items.push({
      id: 'm-chat',
      name: 'Chat',
      icon: MessageCircle,
      href: '/chat',
      compactGroup: 'more',
      requiredPermission: getNavigationItemPermission('m-chat'),
    });
  }

  return items;
};

const buildManagementSection = (config: InternalNavConfig): SidebarNavItem[] => {
  const { viewSettings, hasPermission, userRole } = config;
  const items: SidebarNavItem[] = [];

  items.push({
    name: '--- MANAGEMENT HEADER ---',
    icon: Home,
    href: '#',
    isSectionHeader: true,
    sectionTitle: 'Management',
  });

  items.push({
    id: 'people',
    name: 'People Management',
    compactName: 'People',
    icon: UsersRound,
    href: '/users',
    compactGroup: 'primary',
    requiredPermission: getNavigationItemPermission('people'),
    subItems: [
      { name: 'Users', href: '/users', requiredPermission: getNavigationItemPermission('users') },
      ...(hasPermission('user_management', 'list')
        ? [
            {
              name: 'Pods',
              href: '/users?view=pods',
              requiredPermission: getNavigationItemPermission('pods'),
            },
          ]
        : []),
      {
        name: 'Tags',
        href: '/users?view=tags',
        requiredPermission: getNavigationItemPermission('tags'),
      },
      {
        name: 'Groups',
        href: '/users?view=groups',
        requiredPermission: getNavigationItemPermission('groups'),
      },
      {
        name: 'Corporates',
        href: '/users?view=corporates',
        requiredPermission: { feature: 'corporate_accounts', action: 'list' },
      },
      {
        name: 'Families',
        href: '/users?view=families',
        requiredPermission: { feature: 'family_accounts', action: 'list' },
      },
    ],
  });

  if (viewSettings.showAnalytics) {
    items.push({
      id: 'analytics',
      name: 'Analytics',
      icon: LayoutDashboard,
      href: '/dashboard',
      compactGroup: 'more',
      requiredPermission: getNavigationItemPermission('analytics'),
      subItems: [
        { name: 'Pods', href: '/dashboard' },
        { name: 'Tags', href: '/dashboard?section=tags' },
        { name: 'Groups', href: '/dashboard?section=groups' },
        { name: 'Members', href: '/dashboard?section=members' },
      ],
    });
  }

  items.push({
    id: 'nutrition',
    name: 'Nutrition Planner',
    compactName: 'Nutrition',
    icon: Apple,
    href: '/nutrition/planner',
    compactGroup: 'more',
    requiredPermission: getNavigationItemPermission('nutrition'),
    subItems: [
      { name: 'Overview', href: '/nutrition/planner?tab=overview' },
      { name: 'Foods & Recipes', href: '/nutrition/planner?tab=foods-recipes' },
      { name: 'Plans & Templates', href: '/nutrition/planner?tab=plans-templates' },
      { name: 'Guidance', href: '/nutrition/planner?tab=guidance' },
      { name: 'Tracking', href: '/nutrition/planner?tab=tracking' },
      { name: 'Recommendations', href: '/nutrition/planner?tab=recommendations' },
    ],
  });

  if (viewSettings.showBuilder) {
    items.push({
      id: 'builder',
      name: 'Builder',
      icon: Blocks,
      href: '/builder',
      compactGroup: 'more',
      requiredPermission: getNavigationItemPermission('builder'),
      subItems: [
        { name: 'Conditions', href: '/builder?mode=condition' },
        { name: 'Protocols', href: '/builder?mode=protocol' },
        { name: 'Templates', href: '/builder?mode=template' },
        { name: 'Playground', href: '/builder?mode=playground' },
      ],
    });
  }

  items.push({
    id: 'feedback',
    name: 'Ideas & Issues',
    compactName: 'Feedback',
    icon: Lightbulb,
    href: '/feedback?tab=ideas',
    compactGroup: 'more',
    requiredPermission: getNavigationItemPermission('feedback'),
    subItems: [
      {
        name: 'Ideas',
        href: '/feedback?tab=ideas',
        requiredPermission: getNavigationItemPermission('ideas'),
      },
      {
        name: 'Issues',
        href: '/feedback?tab=issues',
        requiredPermission: getNavigationItemPermission('feedback'),
      },
    ],
  });

  return items;
};

const buildDesktopBottomItems = (
  hasPermission: (
    feature: import('@/utils/permissions').PermissionFeature,
    action: import('@/utils/permissions').PermissionAction
  ) => boolean,
  userRole: string
): SidebarNavItem[] => {
  const items: SidebarNavItem[] = [];

  items.push({
    name: '--- RESOURCES HEADER ---',
    icon: BookOpen,
    href: '#',
    isSectionHeader: true,
    sectionTitle: 'Resources',
  });

  items.push({
    id: 'kb',
    name: 'Knowledge Base',
    compactName: 'KB',
    icon: BookOpen,
    href: '/knowledge-base',
    compactGroup: 'more',
    requiredPermission: getNavigationItemPermission('kb'),
    subItems: [
      { name: 'All Articles', href: '/knowledge-base' },
      { name: 'Bookmarks', href: '/knowledge-base?view=bookmarks' },
      { name: 'Recently Viewed', href: '/knowledge-base?view=recent' },
      { name: "What's New?", href: '/whats-new' },
    ],
  });

  items.push({
    id: 'ref-data',
    name: 'Reference Data',
    compactName: 'Ref Data',
    icon: Database,
    href: '/backoffice',
    compactGroup: 'more',
    requiredPermission: getNavigationItemPermission('ref-data'),
    subItems: [
      {
        name: 'Feature Flags',
        href: '/backoffice?tab=feature-flags-references',
        requiredPermission: getNavigationItemPermission('feature-flags'),
      },
      ...(canAccessBackofficeSection('bookings-dashboard', hasPermission)
        ? [{ name: 'Bookings Dashboard', href: '/backoffice?tab=bookings-dashboard' }]
        : []),
      ...(canAccessBackofficeSection('doctor-assignment', hasPermission)
        ? [{ name: 'Doctor Assignment', href: '/backoffice?tab=doctor-assignment' }]
        : []),
      ...(canAccessBackofficeSection('organization-references', hasPermission)
        ? [
            {
              name: 'Orgs & Partners',
              href: '/backoffice?tab=organization-references',
            },
          ]
        : []),
      {
        name: 'Clinical',
        href: '/backoffice?tab=clinical-references',
        requiredPermission: { feature: 'references_catalog', action: 'list' as const },
      },
      {
        name: 'Medical',
        href: '/backoffice?tab=medical-references',
        requiredPermission: { feature: 'references_catalog', action: 'list' as const },
      },
      {
        name: 'Lifestyle',
        href: '/backoffice?tab=lifestyle-references',
        requiredPermission: { feature: 'references_catalog', action: 'list' as const },
      },
      ...(canAccessBackofficeSection('membership-references', hasPermission)
        ? [
            {
              name: 'Membership',
              href: '/backoffice?tab=membership-references',
            },
          ]
        : []),
      {
        name: 'Workflow',
        href: '/backoffice?tab=workflow-references',
        requiredPermission: getNavigationItemPermission('workflow-tasks'),
      },
      {
        name: 'Communications',
        href: '/backoffice?tab=communications-references',
        requiredPermission: { feature: 'notification_templates', action: 'list' as const },
      },
      {
        name: 'Prompts',
        href: '/backoffice?tab=prompt-management',
        requiredPermission: getNavigationItemPermission('prompt-management'),
      },
    ],
  });

  items.push({
    id: 'platform-chat',
    name: PLATFORM_ASSISTANT_NAME,
    compactName: PLATFORM_ASSISTANT_NAME,
    icon: Bot,
    href: '/platform-chat',
    compactGroup: 'more',
    requiredPermission: getNavigationItemPermission('platform-chat'),
  });

  items.push({
    id: 'public-pages',
    name: 'Public Pages',
    compactName: 'Public',
    icon: Globe,
    href: '#',
    compactGroup: 'more',
    onAction: () => window.open('/public', '_blank'),
    requiredPermission: getNavigationItemPermission('shared-preferences'),
  });

  return items;
};

const MemberContextCard = ({
  client,
  sidebarMode,
  onNavigate,
  onMobileToggle,
  variant,
}: {
  client: { id: string; first_name?: string; last_name?: string };
  sidebarMode?: SidebarMode;
  onNavigate: (path: string) => void;
  onMobileToggle?: () => void;
  variant: 'desktop' | 'mobile';
}) => {
  const fullName = `${client.first_name || ''} ${client.last_name || ''}`.trim();
  const isCollapsed = sidebarMode === 'icon-only';
  const isCompact = sidebarMode === 'compact';

  if (variant === 'mobile') {
    return (
      <div
        className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-xl"
        data-testid="navbar_memberContextCard"
      >
        <div className="flex items-center gap-3 mb-3">
          <img src={Avtar} alt={fullName} className="w-10 h-10 rounded-full flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">
              {toTitleCase(client.first_name ?? null)} {toTitleCase(client.last_name ?? null)}
            </p>
            <p className="text-xs text-slate-500">Viewing as member</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              onNavigate(`/hub?client=${client.id}`);
              onMobileToggle?.();
            }}
            className="flex-1 text-sm font-medium text-slate-700 px-3 py-2 bg-white border border-slate-200 rounded-lg active:bg-slate-100"
            data-testid="navbar_memberContextOpenInHubBtn"
          >
            Open in Hub
          </button>
          <button
            onClick={() => {
              onNavigate('/hub');
              onMobileToggle?.();
            }}
            className="text-sm font-medium text-slate-500 px-3 py-2 active:bg-slate-100 rounded-lg"
            data-testid="navbar_memberContextExitBtn"
          >
            Exit
          </button>
        </div>
      </div>
    );
  }

  if (isCollapsed || isCompact) {
    return (
      <div
        className={cn(
          'mx-2 mt-3 bg-white/10 border border-white/10 rounded-xl p-2',
          isCompact && 'mx-1'
        )}
        data-testid="navbar_memberContextCard"
      >
        <div className="flex flex-col items-center gap-2">
          <img
            src={Avtar}
            alt={fullName}
            className={cn('rounded-full', isCompact ? 'w-7 h-7' : 'w-8 h-8')}
          />
          {isCompact && (
            <span className="text-[10px] font-medium text-white/70 text-center leading-tight line-clamp-1">
              {toTitleCase(client.first_name ?? null)}
            </span>
          )}
          <button
            onClick={() => onNavigate('/hub')}
            className="text-[10px] text-white/60 hover:text-white font-medium px-2 py-1 bg-white/10 border border-white/10 rounded hover:bg-white/15 transition-colors duration-150 motion-reduce:transition-none"
            title="Return to Hub"
            data-testid="navbar_memberContextExitBtn"
          >
            Exit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mx-3 mt-3 bg-white/10 border border-white/10 rounded-xl p-3"
      data-testid="navbar_memberContextCard"
    >
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <img src={Avtar} alt={fullName} className="w-10 h-10 rounded-full flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">
              {toTitleCase(client.first_name ?? null)} {toTitleCase(client.last_name ?? null)}
            </p>
            <p className="text-xs text-white/50">Viewing as member</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onNavigate(`/hub?client=${client.id}`)}
            className="flex-1 text-xs font-medium text-white/80 px-3 py-1.5 bg-white/10 border border-white/10 rounded-lg hover:bg-white/15 transition-colors duration-150 motion-reduce:transition-none"
            data-testid="navbar_memberContextOpenInHubBtn"
          >
            Open in Hub
          </button>
          <button
            onClick={() => onNavigate('/hub')}
            className="text-xs font-medium text-white/50 px-3 py-1.5 hover:text-white/80 hover:bg-white/10 rounded-lg transition-colors duration-150 motion-reduce:transition-none"
            data-testid="navbar_memberContextExitBtn"
          >
            Exit
          </button>
        </div>
      </div>
    </div>
  );
};

const ActingOnBehalfBanner = ({
  name,
  onStop,
  onMobileToggle,
  sidebarMode,
  variant,
}: {
  name: string;
  onStop: () => void;
  onMobileToggle?: () => void;
  sidebarMode?: SidebarMode;
  variant: 'desktop' | 'mobile';
}) => {
  const isCollapsed = sidebarMode === 'icon-only';
  const isCompact = sidebarMode === 'compact';

  if (variant === 'mobile') {
    return (
      <div
        className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-xl"
        data-testid="navbar_actingOnBehalfBanner"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-amber-600" />
            <div>
              <p className="text-xs text-amber-600 font-medium">Acting on behalf</p>
              <p className="text-sm font-semibold text-amber-800">{name}</p>
            </div>
          </div>
          <button
            onClick={() => {
              onStop();
              onMobileToggle?.();
            }}
            className="text-sm text-amber-700 font-medium px-3 py-1.5 bg-amber-100 active:bg-amber-200 rounded-lg"
            data-testid="navbar_actingOnBehalfExitBtn"
          >
            Exit
          </button>
        </div>
      </div>
    );
  }

  if (isCollapsed || isCompact) {
    return (
      <div
        className={cn(
          'mx-2 mt-2 p-1 bg-amber-500/20 border border-amber-400/30 rounded-xl',
          isCompact && 'mx-1'
        )}
        data-testid="navbar_actingOnBehalfBanner"
      >
        <div className="flex flex-col items-center gap-1">
          <Users className="w-4 h-4 text-amber-300" />
          <button
            onClick={onStop}
            className="text-[10px] text-amber-300 hover:text-amber-200 font-medium"
            title="Exit managing mode"
            data-testid="navbar_actingOnBehalfExitBtn"
          >
            Exit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mx-3 mt-2 p-2 bg-amber-500/20 border border-amber-400/30 rounded-xl"
      data-testid="navbar_actingOnBehalfBanner"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Users className="w-4 h-4 text-amber-300 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] text-amber-300 font-medium">Acting on behalf</p>
            <p className="text-xs font-semibold text-amber-200 truncate">{name}</p>
          </div>
        </div>
        <button
          onClick={onStop}
          className="text-xs text-amber-300 hover:text-amber-200 font-medium px-2 py-1 hover:bg-amber-500/20 rounded flex-shrink-0 transition-colors duration-150 motion-reduce:transition-none"
          data-testid="navbar_actingOnBehalfExitBtn"
        >
          Exit
        </button>
      </div>
    </div>
  );
};

export const InternalSidebar: React.FC<InternalSidebarProps> = ({
  sidebarMode,
  onToggle,
  isMobileOpen,
  onMobileToggle,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const urlClientId = pathname.match(/\/member\/[^/]+\/([0-9a-f-]{36})/)?.[1] || '';
  const { client } = useMemberClient(null, null, urlClientId);

  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isQuickLinksDrawerOpen, setQuickLinksDrawerOpen] = useState(false);
  const [progressText, setProgressText] = useState('0/7');
  const [hasConsultationVideos, setHasConsultationVideos] = useState<boolean | null>(null);

  const userRole = useSelector((state: RootState) => state.user.role) || '';
  const { hasPermission } = usePermissions();
  const username = useSelector((state: RootState) => state.user.username);
  const firstName = useSelector((state: RootState) => state.user.first_name);
  const lastName = useSelector((state: RootState) => state.user.last_name);

  const { flags } = useFeatureFlagsContext();
  const { viewSettings } = useViewSettings();
  const { isActingOnBehalf, targetMember, stopActing } = useActingOnBehalf();
  const { data: uiPrefs } = useUiPreferences();

  const featureFlags = useMemo(
    () => ({
      showHealthSnapshot: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_HEALTH_SNAPSHOT),
      showMemberMarketplace: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_MEMBER_MARKETPLACE),
      showFamilyManagement: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_FAMILY_MANAGEMENT),
      showNotifications: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_NOTIFICATIONS),
      showMemberCarts: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_MEMBER_CARTS),
      showWearables: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_WEARABLES),
      showDailyLog: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_DAILY_LOG),
      showClientTickets: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_CLIENT_TICKETS),
      showInAppChat: true,
      showGiftMembership: false,
      isMemberVideosFeatureEnabled: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_MEMBER_VIDEOS_TAB),
    }),
    [flags]
  );

  const clientId = client?.id || urlClientId;

  const formattedUserName =
    `${toTitleCase(firstName)} ${toTitleCase(lastName)}`.trim() || username || 'User';

  useEffect(() => {
    const fetchProgramData = async () => {
      if (!client?.id) return;

      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token) return;

        const response = await getUserProgramEnrollments(client.id, token);
        const enrollments = Array.isArray(response.data)
          ? response.data
          : response.data?.results || response.data?.enrollments || [];

        const thriveEnrollment = enrollments.find(
          (enrollment: ProgramEnrollment) =>
            isThriveProgram(enrollment.program_name) && enrollment.is_active
        );

        if (thriveEnrollment?.program_data?.program_steps) {
          const progress = calculateProgramProgress(
            thriveEnrollment.program_data.program_steps,
            thriveEnrollment.program_name,
            thriveEnrollment.show_intake_call === 'true',
            client.addon_names
          );
          setProgressText(progress.progressText);
        }
      } catch (error) {
        logger.error('Error fetching program data for sidebar', error);
      }
    };

    void fetchProgramData();
  }, [client?.id, client?.addon_names]);

  useEffect(() => {
    if (!featureFlags.isMemberVideosFeatureEnabled) {
      setHasConsultationVideos(false);
      return;
    }

    if (!clientId) {
      setHasConsultationVideos(null);
      return;
    }

    const abortController = new AbortController();

    const fetchVideos = async () => {
      setHasConsultationVideos(null);
      try {
        const response = await listConsultationVideos(
          { shared_only: true, client_id: clientId },
          abortController.signal
        );
        if (!abortController.signal.aborted) {
          setHasConsultationVideos(normalizePaginated(response).length > 0);
        }
      } catch (error) {
        if (abortController.signal.aborted || axios.isCancel(error)) {
          return;
        }
        logger.error('Error fetching consultation videos for internal sidebar', error);
        setHasConsultationVideos(true);
      }
    };

    void fetchVideos();

    return () => {
      abortController.abort();
    };
  }, [clientId, featureFlags.isMemberVideosFeatureEnabled]);

  const shouldShowVideos =
    featureFlags.isMemberVideosFeatureEnabled && hasConsultationVideos !== false;

  const navConfig: InternalNavConfig = useMemo(
    () => ({
      userRole,
      clientId,
      badgeText: progressText,
      hasPermission,
      viewSettings: {
        showAnalytics: viewSettings.showAnalytics,
        showBuilder: viewSettings.showBuilder,
      },
      featureFlags: {
        ...featureFlags,
        showVideos: shouldShowVideos,
      },
    }),
    [userRole, clientId, progressText, hasPermission, viewSettings, featureFlags, shouldShowVideos]
  );

  const showUtilityButtons = hasPermission('permissions_me', 'read');

  const utilityNavItems: SidebarNavItem[] = useMemo(
    () => [
      {
        id: 'search',
        name: 'Search',
        icon: Search,
        href: '#',
        compactGroup: 'more' as CompactGroup,
        shortcut: '⌘J · ⌘P',
        onAction: () => window.dispatchEvent(new CustomEvent('openUniversalSearch')),
      },
      {
        id: 'display',
        name: 'Display Preferences',
        compactName: 'Display',
        icon: Eye,
        href: '#',
        compactGroup: 'more' as CompactGroup,
        onAction: () => window.dispatchEvent(new CustomEvent('openViewSettings')),
      },
      {
        id: 'gcal',
        name: 'Google Calendar',
        compactName: 'Calendar',
        icon: Calendar,
        href: '#',
        compactGroup: 'more' as CompactGroup,
        onAction: () => window.dispatchEvent(new CustomEvent('openCalendarModal')),
      },
    ],
    []
  );

  const workspaceItems = useMemo(
    () => filterNavigationByPermission(buildWorkspaceSection(navConfig), hasPermission),
    [navConfig, hasPermission]
  );
  const memberPreviewItems = useMemo(
    () => filterNavigationByPermission(buildMemberPreviewSection(navConfig), hasPermission),
    [navConfig, hasPermission]
  );
  const managementItems = useMemo(
    () => filterNavigationByPermission(buildManagementSection(navConfig), hasPermission),
    [navConfig, hasPermission]
  );
  const desktopBottomItems = useMemo(
    () =>
      filterNavigationByPermission(buildDesktopBottomItems(hasPermission, userRole), hasPermission),
    [hasPermission, userRole]
  );

  // Pass `flags` through so saved Quick Links targeting feature-flag-gated
  // routes vanish when the corresponding flag is off, matching the rest
  // of the InternalSidebar's gating. (M2)
  // Pass every nav item so a Quick Link to a page already in the side nav
  // (Hub, Business Dashboard, Appointments, …) moves that entry to the top
  // instead of rendering a second copy of it.
  const quickLinksSection = useMemo(
    () =>
      buildQuickLinksSection({
        links: uiPrefs.quick_links,
        hasPermission,
        clientId,
        flags,
        navItems: [
          ...workspaceItems,
          ...memberPreviewItems,
          ...managementItems,
          ...desktopBottomItems,
        ],
      }),
    [
      uiPrefs.quick_links,
      hasPermission,
      clientId,
      flags,
      workspaceItems,
      memberPreviewItems,
      managementItems,
      desktopBottomItems,
    ]
  );

  // Entries promoted into Quick Links leave their original position.
  const withoutPromoted = useCallback(
    (items: SidebarNavItem[]) =>
      items.filter((item) => !quickLinksSection.promotedHrefs.has(item.href)),
    [quickLinksSection]
  );

  const quickLinksWithSeparator = useMemo<SidebarNavItem[]>(
    () =>
      quickLinksSection.items.length > 0
        ? [...quickLinksSection.items, QUICK_LINKS_SEPARATOR]
        : quickLinksSection.items,
    [quickLinksSection]
  );

  const queryClient = useQueryClient();
  const prefetchBuilderNavData = useCallback(() => {
    prefetchConditionBuilderData(queryClient);
  }, [queryClient]);

  const withBuilderPrefetch = useCallback(
    (items: SidebarNavItem[]) =>
      items.map((item) =>
        item.id === 'builder' ? { ...item, onPrefetch: prefetchBuilderNavData } : item
      ),
    [prefetchBuilderNavData]
  );

  const desktopNavItems = useMemo(
    () =>
      withBuilderPrefetch([
        ...quickLinksWithSeparator,
        ...withoutPromoted(workspaceItems),
        ...withoutPromoted(memberPreviewItems),
        ...withoutPromoted(managementItems),
        ...withoutPromoted(desktopBottomItems),
        ...(showUtilityButtons ? utilityNavItems : []),
      ]),
    [
      withBuilderPrefetch,
      quickLinksWithSeparator,
      withoutPromoted,
      workspaceItems,
      memberPreviewItems,
      managementItems,
      desktopBottomItems,
      showUtilityButtons,
      utilityNavItems,
    ]
  );

  const {
    pinnedItems,
    unpinnedItems,
    pin,
    unpin,
    moveUp,
    moveDown,
    resetToDefaults,
    pinAll,
    unpinAll,
  } = useSidebarPins(desktopNavItems, 'sidebar-pins-internal');

  const pinControls = useMemo(
    () => ({
      pinnedItems,
      unpinnedItems,
      pin,
      unpin,
      moveUp,
      moveDown,
      resetToDefaults,
      pinAll,
      unpinAll,
    }),
    [pinnedItems, unpinnedItems, pin, unpin, moveUp, moveDown, resetToDefaults, pinAll, unpinAll]
  );

  const mobileNavItems = useMemo(
    () =>
      withBuilderPrefetch([
        ...quickLinksWithSeparator,
        ...withoutPromoted(workspaceItems),
        ...withoutPromoted(memberPreviewItems),
        ...withoutPromoted(managementItems),
        ...withoutPromoted(desktopBottomItems),
      ]),
    [
      withBuilderPrefetch,
      quickLinksWithSeparator,
      withoutPromoted,
      workspaceItems,
      memberPreviewItems,
      managementItems,
      desktopBottomItems,
    ]
  );

  const isProfileActive = pathname.startsWith('/profiles/');

  const handleProfileClick = () => {
    if (username) {
      navigate(`/profiles/${username}/edit`);
    }
  };

  const handleConfirmLogout = async () => {
    try {
      await signOutSession({ queryClient });
    } catch (error) {
      logger.error('Error signing out', error);
    } finally {
      setLogoutModalOpen(false);
      navigate('/login');
    }
  };

  const handleLogoutButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setLogoutModalOpen(true);
  };

  const widthConfig = SIDEBAR_WIDTHS[sidebarMode];
  const sidebarStyle = getSidebarStyle();
  const sidebarBgColor = getSidebarBgColor() ?? (sidebarStyle === 'light' ? undefined : '#152319');

  return (
    <>
      <Sheet
        open={isMobileOpen}
        onOpenChange={(open) => {
          if (open !== isMobileOpen) onMobileToggle();
        }}
      >
        <SheetContent
          side="bottom"
          showCloseButton={false}
          aria-describedby={undefined}
          className="h-[88dvh] rounded-t-3xl p-0"
          data-testid="navbar_mobileMenu"
        >
          <div className="sticky top-0 z-10 bg-background px-5 pb-3 pt-3">
            <div
              className="mx-auto mb-3 h-1 w-10 rounded-full bg-[#152319]/20"
              aria-hidden="true"
            />
            <div className="flex items-center justify-between">
              <SheetTitle>Workspace</SheetTitle>
              <button
                onClick={onMobileToggle}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-[#152319]/5"
                aria-label="Close menu"
                data-testid="navbar_mobileCloseBtn"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
            {clientId && client && (
              <MemberContextCard
                client={{
                  id: client.id,
                  first_name: client.first_name,
                  last_name: client.last_name,
                }}
                onNavigate={navigate}
                onMobileToggle={onMobileToggle}
                variant="mobile"
              />
            )}

            {isActingOnBehalf && targetMember && (
              <ActingOnBehalfBanner
                name={targetMember.name}
                onStop={stopActing}
                onMobileToggle={onMobileToggle}
                variant="mobile"
              />
            )}

            <nav className="flex flex-col space-y-1 mb-4" data-testid="navbar_mobileNav">
              <SidebarNavList
                items={mobileNavItems}
                pathname={pathname}
                onItemClick={onMobileToggle}
                variant="mobile"
                testIdPrefix="navbar"
              />
            </nav>
            <SidebarProfile
              displayName={formattedUserName}
              subtitle={formatRole(userRole)}
              avatarUsername={username}
              onProfileClick={handleProfileClick}
              onLogoutClick={handleLogoutButtonClick}
              variant="mobile"
              testIdPrefix="navbar"
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar */}
      <aside
        className={cn(
          'hidden lg:flex flex-shrink-0 flex-col border-r border-black/[0.06] transition-[width] duration-[320ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none h-[100dvh] min-h-0 overflow-hidden will-change-[width] relative z-10',
          sidebarStyle === 'light' ? 'bg-[#FAF6F1]' : 'bg-primary',
          widthConfig.className
        )}
        data-sidebar-style={sidebarStyle}
        data-testid="navbar_desktopSidebar"
        style={sidebarBgColor ? { backgroundColor: sidebarBgColor } : undefined}
      >
        <SidebarLogo sidebarMode={sidebarMode} onToggle={onToggle} testIdPrefix="navbar" />

        {clientId && client && (
          <MemberContextCard
            client={{ id: client.id, first_name: client.first_name, last_name: client.last_name }}
            sidebarMode={sidebarMode}
            onNavigate={navigate}
            variant="desktop"
          />
        )}

        {isActingOnBehalf && targetMember && (
          <ActingOnBehalfBanner
            name={targetMember.name}
            onStop={stopActing}
            sidebarMode={sidebarMode}
            variant="desktop"
          />
        )}

        <ScrollArea
          type="auto"
          className="sidebar-scroll-area min-h-0 flex-1"
          data-testid="navbar_navScrollArea"
        >
          <nav
            className={cn(
              'py-2',
              sidebarMode === 'compact' ? 'space-y-0.5 px-2' : 'space-y-0.5',
              sidebarMode === 'full' ? 'px-4' : sidebarMode === 'icon-only' ? 'px-2' : ''
            )}
            aria-label="Main navigation"
            data-testid="navbar_desktopNav"
          >
            <SidebarNavList
              items={desktopNavItems}
              pathname={pathname}
              sidebarMode={sidebarMode}
              onToggle={onToggle}
              variant="desktop"
              pinControls={pinControls}
              testIdPrefix="navbar"
            />
          </nav>
        </ScrollArea>
        <div
          className={cn(
            'shrink-0 space-y-2 border-t border-white/10',
            sidebarMode === 'full' ? 'p-3' : 'p-2'
          )}
          data-testid="navbar_bottomSection"
        >
          {userRole !== 'CLIENT' && (
            <button
              type="button"
              onClick={() => setQuickLinksDrawerOpen(true)}
              title="Quick links"
              aria-label="Open quick links"
              data-testid="navbar_quickLinksBtn"
              className={cn(
                'w-full flex items-center text-sm font-medium rounded-3xl transition-colors duration-150 ease motion-reduce:transition-none text-white/70 hover:bg-white/10 hover:text-white',
                sidebarMode === 'compact'
                  ? 'flex-col gap-0.5 px-1 py-1.5 rounded-xl'
                  : 'px-3 py-2.5',
                sidebarMode === 'icon-only' && 'justify-center'
              )}
            >
              <Star
                className={cn(sidebarMode === 'compact' ? 'w-4 h-4' : 'w-5 h-5', 'flex-shrink-0')}
              />
              {sidebarMode === 'compact' ? (
                <span className="text-[9px] font-medium leading-tight">Quick</span>
              ) : (
                <span className={cn('ml-3', sidebarMode === 'icon-only' && 'hidden')}>
                  Quick links
                </span>
              )}
            </button>
          )}
          <SidebarProfile
            displayName={formattedUserName}
            subtitle={formatRole(userRole)}
            avatarUsername={username}
            sidebarMode={sidebarMode}
            isActive={isProfileActive}
            onProfileClick={handleProfileClick}
            onLogoutClick={handleLogoutButtonClick}
            variant="desktop"
            testIdPrefix="navbar"
          />
        </div>
      </aside>

      <QuickLinksDrawer
        open={isQuickLinksDrawerOpen}
        onOpenChange={setQuickLinksDrawerOpen}
        clientId={clientId ?? ''}
      />

      <div data-testid="navbar_logoutModal">
        <MemberDefaultModel
          isOpen={isLogoutModalOpen}
          onClose={() => setLogoutModalOpen(false)}
          onConfirm={handleConfirmLogout}
          icon={Logout}
          title="Log out ?"
          description="Are you sure you want to logout?"
          confirmButtonText="Log out"
          iconBgClassName="bg-gray-100"
          confirmButtonClassName="bg-black hover:bg-black"
        />
      </div>
    </>
  );
};
